import React from "react";
import { Link } from "react-router-dom";

// External courses live on csonlineschool.com.au. HD Class is the only
// course that runs on this site (cseducation.com.au) — internal route.
const CSONLINE_BASE = "https://csonlineschool.com.au";

const primaryYears = [
  {
    year: "Year 1",
    subjects: [{ name: "WRMT", external: true }],
  },
  {
    year: "Year 2",
    subjects: [{ name: "WRMT", external: true }],
  },
  {
    year: "Year 3",
    subjects: [
      { name: "WRMT", external: true },
      { name: "OC Preliminary Trial Tests", external: true },
    ],
  },
  {
    year: "Year 4",
    subjects: [
      { name: "WRMT", external: true },
      { name: "OC Trial Tests", external: true },
    ],
  },
  {
    year: "Year 5",
    subjects: [
      { name: "WRMT", external: true },
      { name: "Selective Preliminary/Trial Tests", external: true },
      { name: "HD Class", external: false },
    ],
  },
  {
    year: "Year 6",
    subjects: [
      { name: "WRMT", external: true },
      { name: "Selective Trial Tests", external: true },
      { name: "High School Preparation", external: true },
    ],
  },
];

// Y7–Y10 are operated on this site, so they link to the internal /our-courses
// page. Y11–Y12 will be added here later.
const highYears = [
  {
    year: "Year 7",
    subjects: [
      { name: "English & Writing", external: false },
      { name: "Maths", external: false },
      { name: "Science", external: false },
    ],
  },
  {
    year: "Year 8",
    subjects: [
      { name: "English & Writing", external: false },
      { name: "Maths", external: false },
      { name: "Science", external: false },
    ],
  },
  {
    year: "Year 9",
    subjects: [
      { name: "English & Writing", external: false },
      { name: "Maths", external: false },
      { name: "Science", external: false },
    ],
  },
  {
    year: "Year 10",
    subjects: [
      { name: "English & Writing", external: false },
      { name: "Maths", external: false },
      { name: "Science", external: false },
    ],
  },
];

const styles = {
  section: {
    background: "#A6192E",
    padding: "96px 0",
    color: "#ffffff",
  },
  container: {
    maxWidth: 1320,
    margin: "0 auto",
    padding: "0 24px",
  },
  header: {
    textAlign: "center",
    maxWidth: 880,
    margin: "0 auto 48px",
  },
  eyebrow: {
    display: "inline-block",
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: "1.5px",
    textTransform: "uppercase",
    color: "rgba(255,255,255,0.75)",
    marginBottom: 12,
  },
  heading: {
    fontSize: 36,
    fontWeight: 800,
    color: "#ffffff",
    margin: "0 0 16px",
    letterSpacing: "-0.5px",
    whiteSpace: "nowrap",
  },
  subheading: {
    fontSize: 16,
    lineHeight: 1.6,
    color: "rgba(255,255,255,0.85)",
    margin: 0,
  },
  rowLabel: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    fontSize: 13,
    fontWeight: 600,
    letterSpacing: "1px",
    textTransform: "uppercase",
    color: "rgba(255,255,255,0.85)",
    margin: "32px 0 16px",
  },
  rowLabelLine: {
    flex: 1,
    height: 1,
    background: "rgba(255,255,255,0.20)",
  },
  grid: {
    display: "grid",
    // Fixed 6 columns so Year 1–6 line up neatly in one row, leaving room
    // to add Year 11 and Year 12 to the high-school row later.
    gridTemplateColumns: "repeat(6, minmax(0, 1fr))",
    gap: 16,
  },
  card: {
    background: "rgba(255,255,255,0.10)",
    border: "1px solid rgba(255,255,255,0.22)",
    borderRadius: 12,
    padding: "24px 22px",
    display: "flex",
    flexDirection: "column",
    transition: "transform 0.2s, border-color 0.2s",
  },
  cardTag: {
    display: "inline-block",
    alignSelf: "flex-start",
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.5px",
    textTransform: "uppercase",
    color: "rgba(255,255,255,0.75)",
    background: "rgba(166,25,46,0.12)",
    padding: "4px 10px",
    borderRadius: 999,
    marginBottom: 14,
  },
  cardYear: {
    fontSize: 22,
    fontWeight: 800,
    color: "#ffffff",
    margin: "0 0 14px",
    letterSpacing: "-0.3px",
  },
  subjectList: {
    listStyle: "none",
    padding: 0,
    margin: 0,
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  subjectItem: {
    fontSize: 13,
    color: "#e2e8f0",
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 8px",
    borderRadius: 6,
    textDecoration: "none",
    transition: "background 0.15s, color 0.15s",
  },
  subjectDot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.7)",
    flexShrink: 0,
  },
  extIcon: {
    marginLeft: "auto",
    opacity: 0.6,
    flexShrink: 0,
  },
};

const Subject = ({ name, external }) => {
  const inner = (
    <>
      <span style={styles.subjectDot}></span>
      <span>{name}</span>
      {external && (
        <svg
          style={styles.extIcon}
          width="11"
          height="11"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
          <polyline points="15 3 21 3 21 9" />
          <line x1="10" y1="14" x2="21" y2="3" />
        </svg>
      )}
    </>
  );
  return external ? (
    <a
      href={CSONLINE_BASE}
      target="_blank"
      rel="noopener noreferrer"
      style={styles.subjectItem}
      className="subject-link"
    >
      {inner}
    </a>
  ) : (
    <Link to="/our-courses" style={styles.subjectItem} className="subject-link">
      {inner}
    </Link>
  );
};

const YearRow = ({ items }) => (
  <div style={styles.grid}>
    {items.map((y) => (
      <div key={y.year} style={styles.card} className="year-card">
        <h3 style={styles.cardYear}>{y.year}</h3>
        <ul style={styles.subjectList}>
          {y.subjects.map((s) => (
            <li key={s.name}>
              <Subject name={s.name} external={s.external} />
            </li>
          ))}
        </ul>
      </div>
    ))}
  </div>
);

function YearCourses() {
  return (
    <section style={styles.section}>
      <div style={styles.container}>
        <div style={styles.header}>
          <span style={styles.eyebrow}>Courses</span>
          <h2 style={styles.heading} className="year-heading">
            Tailored programs from primary to high school
          </h2>
          <p style={styles.subheading}>
            Structured courses designed for each year level and subject area.
          </p>
        </div>

        <div style={styles.rowLabel}>
          <span style={styles.rowLabelLine}></span>
          <span>Primary (Year 1–6)</span>
          <span style={styles.rowLabelLine}></span>
        </div>
        <YearRow items={primaryYears} />

        <div style={styles.rowLabel}>
          <span style={styles.rowLabelLine}></span>
          <span>High School (Year 7–10)</span>
          <span style={styles.rowLabelLine}></span>
        </div>
        <YearRow items={highYears} />
      </div>

      <style>{`
        .year-card:hover {
          transform: translateY(-3px);
          border-color: rgba(255,255,255,0.65) !important;
        }
        .subject-link:hover {
          background: rgba(166,25,46,0.08);
          color: #ffffff !important;
        }
        /* responsive grid: collapse columns gracefully */
        @media (max-width: 1200px) {
          section [style*="repeat(6"] {
            grid-template-columns: repeat(4, minmax(0, 1fr)) !important;
          }
        }
        @media (max-width: 900px) {
          section [style*="repeat(6"] {
            grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
          }
        }
        @media (max-width: 640px) {
          section [style*="repeat(6"] {
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
          }
          .year-heading {
            white-space: normal !important;
            font-size: 28px !important;
          }
        }
      `}</style>
    </section>
  );
}

export default YearCourses;
